import { navigate } from '../lib/route.js';

// The rules article, transcribed from handleiding.md (repo root). Static content
// — no server call, no per-preset logic. Shared by the standalone HelpScreen
// (reached from the splash) and the in-game help modal (see GameScreen), so a
// player can read the basics before OR mid-game without leaving their game.
//
// animInNewTab: in-game the animation opens in a NEW tab, so a running game is
// never reloaded/reconnected just to watch a clip. From the splash/help there's
// no game to lose, so it stays same-tab (cleaner; the animation has its own back
// button). See the design note in the briefing.
export function HelpContent({
  animInNewTab = false,
}: {
  animInNewTab?: boolean;
}): React.JSX.Element {
  return (
    <article className="help">
      {/* The voice-over animation is a standalone static page at /uitleg/. */}
      <a
        className="help-anim-cta"
        href="/uitleg/"
        {...(animInNewTab ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
      >
        <span className="help-anim-ic" aria-hidden="true">
          ▶
        </span>
        <span className="help-anim-txt">
          <strong>Bekijk de uitleg-animatie</strong>
          <span>
            Korte visuele uitleg mét stem —{' '}
            {animInNewTab ? 'opent in een nieuw tabblad.' : 'met een terug-knop.'}
          </span>
        </span>
      </a>

      <p className="help-lead">
        Je hoeft dit niet uit je hoofd te leren — de app vertelt je op elk moment wat je moet doen.
        Dit geeft je het grote plaatje.
      </p>

      <section className="help-sec">
        <h2>Het idee</h2>
        <p>
          Jullie houden samen het dorp <strong>Willowbrook</strong> veilig door ’s nachts een keten{' '}
          <strong>wachtvuren</strong> brandend te houden tegen de oprukkende duisternis. Maar een
          paar spelers zijn stiekem <strong>shapeshifters</strong> die de vuren willen laten doven —
          en niemand weet wie. Jij weet alleen je eigen rol.
        </p>
      </section>

      <section className="help-sec">
        <h2>De twee teams</h2>
        <ul>
          <li>
            <strong>De eerlijken (vuurwachters)</strong> — de goede meerderheid. Doel: de vuren
            brandend houden én de shapeshifters ontmaskeren.
          </li>
          <li>
            <strong>De shapeshifters</strong> — de verborgen minderheid. Doel: genoeg vuren laten
            doven zonder gepakt te worden. Ze kennen elkaar niet vanaf de start.
          </li>
        </ul>
      </section>

      <section className="help-sec">
        <h2>De dag in vier beats</h2>
        <ol className="help-cycle">
          <li>
            <span className="hc-nm">Ochtend</span>
            <span className="hc-tx">de onthulling: wat gebeurde er ’s nachts — jouw bewijs.</span>
          </li>
          <li>
            <span className="hc-nm">Dag</span>
            <span className="hc-tx">praat en beschuldig, en kies je post voor vannacht.</span>
          </li>
          <li>
            <span className="hc-nm">Schemer</span>
            <span className="hc-tx">laatste overleg én de anonieme stemronde.</span>
          </li>
          <li>
            <span className="hc-nm">Nacht</span>
            <span className="hc-tx">alles op slot; de vuren worden bij dageraad opgelost.</span>
          </li>
        </ol>
        <p className="help-cycle-note">
          In het spel zie je bovenaan altijd wáár in de dag je bent.
        </p>
      </section>

      <section className="help-sec">
        <h2>Zo verloopt een dag</h2>
        <ol>
          <li>
            <strong>Overdag praat je.</strong> Op het <strong>dorpsplein</strong> (iedereen) en in{' '}
            <strong>locaties</strong> (kleiner) overleg je, beschuldig je, verdedig je je. Noem
            iemand met <strong>@naam</strong> en die krijgt een seintje.
          </li>
          <li>
            <strong>Kies je post.</strong> Je claimt een <strong>vuur</strong> waar je die nacht de
            wacht houdt. <strong>Waar je staat is voor iedereen zichtbaar</strong> — maar{' '}
            <strong>wát je daar doet (voeden of saboteren) is geheim.</strong>
          </li>
          <li>
            <strong>’s Nachts</strong> wordt het opgelost: wie waar stond bepaalt of een vuur{' '}
            <strong>blijft branden, gaat flikkeren of dooft.</strong>
          </li>
          <li>
            <strong>Stemmen.</strong> Aan het eind van de dag stem je anoniem wie je wegstuurt. Je
            ziet de <strong>aantallen</strong> (“3 op Anna”), niet wie op wie stemde.
          </li>
          <li>
            <strong>De ochtend onthult</strong> wat er met de vuren gebeurde — dat is je bewijs: een
            gedoofd vuur wijst naar wie daar postte.
          </li>
        </ol>
      </section>

      <section className="help-sec">
        <h2>Hoe win je?</h2>
        <ul>
          <li>
            <strong>De eerlijken winnen</strong> als de keten vuren een afgesproken aantal dagen
            brandend blijft (of als alle shapeshifters weg zijn).
          </li>
          <li>
            <strong>De shapeshifters winnen</strong> als er genoeg vuren doven vóór dat lukt.
          </li>
        </ul>
        <p>
          <strong>Rollen blijven geheim tot het eind</strong> — dan pas zie je wie wat was.
          Ontmaskeren doe je met logica, niet met zekerheid.
        </p>
      </section>

      <section className="help-sec help-kern">
        <h2>De kern in één zin</h2>
        <p>
          Waar je post is bewijs; wat je daar doet is geheim — dus overtuig de anderen dat je te
          vertrouwen bent, en ontmasker de shapeshifters voordat de vuren doven.
        </p>
      </section>

      <details className="help-sec help-grows">
        <summary>Het groeit mee — extra’s per niveau</summary>
        <p>
          Het spel heeft <strong>niveaus</strong> (de host kiest): <strong>Introductie</strong>{' '}
          (kale basis) → <strong>Familie</strong> → <strong>Klassiek</strong> →{' '}
          <strong>Uitdaging</strong>. Hoe hoger, hoe meer extra’s, zoals:
        </p>
        <ul>
          <li>
            <strong>Speciale rollen</strong> — de <strong>Ziener</strong> (onderzoekt ’s nachts één
            speler), de <strong>Beschermer</strong> (schermt een vuur af), de{' '}
            <strong>Schaduw</strong> (een kwade troef die een onschuldige verdacht laat lijken).
          </li>
          <li>
            <strong>Inzet</strong> — kies hóé hard je voedt of saboteert.
          </li>
          <li>
            En meer. Maar de app legt elke extra uit op het moment dat je ’m nodig hebt — dus begin
            gerust op <strong>Introductie</strong> of <strong>Familie</strong>.
          </li>
        </ul>
      </details>

      <section className="help-sec">
        <h2>Tips</h2>
        <ul>
          <li>
            Let op <strong>wie waar stond</strong> bij een vuur dat doofde — daar zit je verdachte.
          </li>
          <li>
            Als jouw vuur onterecht sputtert: zeg het! (“Ik voedde echt.”) Je kunt het niet bewijzen
            — en dat maakt het juist spannend.
          </li>
          <li>
            Het is een teamspel: je wint of verliest <strong>samen</strong> met je team.
          </li>
        </ul>
      </section>
    </article>
  );
}

// Standalone page reached from the splash. Back returns to the splash.
export function HelpScreen(): React.JSX.Element {
  return (
    <main className="help-screen">
      <button className="back" onClick={() => navigate({ name: 'splash' })}>
        ← terug
      </button>
      <h1>Hoe werkt het?</h1>
      <HelpContent />
    </main>
  );
}
